import { FirebaseAnalytics } from '@capacitor-firebase/analytics';

import { Logger } from '@mopla/logger';

import { setFirebaseUserId } from './firebaseUserId';

/**
 * Forwards dataLayer events to the native Firebase Analytics SDK.
 *
 * Native apps do not load GTM (see initUserTracking) — on iOS its transport
 * never reaches GA4 from the "capacitor://" origin anyway. The dataLayer stays
 * in place as the platform-neutral event bus that all push helpers in
 * ./googleTagManager write to, and this bridge is the native reader of it.
 *
 * The events go to the native Firebase instance, which the iOS/Android shell
 * starts from `GoogleService-Info.plist` / `google-services.json`, so they are
 * attributed to the project's iOS/Android app ("…:ios:…" / "…:android:…").
 * That instance is configured automatically at app start by the
 * @capacitor-firebase plugins, so nothing needs to be initialised here.
 * This is unrelated to the Firebase JS SDK instance from initFirebaseApp: that
 * one is used for auth only and identifies as the Web app ("…:web:…"). Both
 * share the same Firebase project but do not depend on each other.
 *
 * Consent is applied to the SDK separately (see ./firebaseConsent), which then
 * decides itself what it sends (Consent Mode, advanced implementation). Only
 * the user id is gated (see ./firebaseUserId), as Consent Mode does not
 * cover it. initUserTracking installs the bridge only once the stored consent
 * is applied; everything pushed before is still in the dataLayer and
 * forwarded on installation.
 */

type TFirebasePrimitive = string | number | boolean;
type TFirebaseParams = Record<
	string,
	TFirebasePrimitive | Record<string, TFirebasePrimitive>[]
>;

const isPrimitive = (v: unknown): v is TFirebasePrimitive =>
	typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean';

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
	typeof v === 'object' && v !== null && !Array.isArray(v);

const pickPrimitives = (obj: Record<string, unknown>) => {
	const result: Record<string, TFirebasePrimitive> = {};
	for (const [k, v] of Object.entries(obj)) {
		if (isPrimitive(v)) result[k] = v;
	}
	return result;
};

/**
 * Firebase Analytics accepts primitive param values plus arrays of flat
 * objects (e.g. the e-commerce `items`). Everything else — nested objects,
 * nested values inside items, arrays of primitives — is skipped, as the native
 * SDKs would drop it anyway (Android's bundle conversion can even fail on it).
 */
const toFirebaseParams = (source: Record<string, unknown>) => {
	const params: TFirebaseParams = {};
	for (const [k, v] of Object.entries(source)) {
		if (k === 'event' || k === 'ecommerce') continue;
		if (isPrimitive(v)) {
			params[k] = v;
		} else if (Array.isArray(v)) {
			const items = v.filter(isPlainObject).map(pickPrimitives);
			if (items.length > 0) params[k] = items;
		}
	}
	return params;
};

export function bridgeDataLayerToFirebase() {
	const logger = Logger.getInstance('UserTracking');

	logger.debug('Firebase Analytics bridge activated');

	const forwardToFirebase = (item: Record<string, unknown>) => {
		const eventName = String(item['event'] ?? '');
		if (!eventName) return;

		// Skip consent-management events
		if (eventName.startsWith('CookieScript')) {
			return;
		}

		// The user id is SDK state, not an event — see ./firebaseUserId.
		if (eventName === 'user_id_set') {
			setFirebaseUserId(item['user_id']);
			return;
		}

		// GTM expects e-commerce data nested under `ecommerce`, the native SDK
		// expects currency / value / items as top-level event params.
		const ecommerce = item['ecommerce'];
		const params = toFirebaseParams({
			...item,
			...(isPlainObject(ecommerce) ? ecommerce : {}),
		});

		FirebaseAnalytics.logEvent({ name: eventName, params }).catch(
			(err: unknown) =>
				logger.error('Firebase bridge: logEvent failed', err, { eventName })
		);
	};

	const processItems = (items: unknown[]) => {
		for (const item of items) {
			if (
				item &&
				typeof item === 'object' &&
				!Array.isArray(item) &&
				'event' in item
			) {
				forwardToFirebase(item as Record<string, unknown>);
			}
		}
	};

	// Anything pushed before the bridge was installed is still sitting in the
	// array — drain it first so early events are not lost.
	processItems([...window.dataLayer]);

	const rawPush = Array.prototype.push.bind(window.dataLayer);

	window.dataLayer.push = function (...items: unknown[]): number {
		const result = rawPush(...items);
		processItems(items);
		return result;
	} as typeof Array.prototype.push;

	logger.debug('Firebase Analytics bridge ready');
}
